/**
 * Receipt Detail View
 *
 * Displays and allows editing of a receipt with:
 * - Receipt image display
 * - Editable form fields (vendor, date, amount, etc.)
 * - Expense type selector with AI suggestions
 * - Save/cancel actions
 */

import { createSidebarLayout } from '../components/sidebar-layout';
import { createFormField } from '../components/form-field';
import { Input } from '../components/input';
import { ExpenseTypeSelector } from '../components/expense-type-selector';
import { createExpenseTypeHelp } from '../components/expense-type-help';
import { createVatSelector } from '../components/vat-selector';
import { showToast } from '../components/toast';
import { renderIcon } from '../utils/icons';
import { expenseReportsStore } from '../services/expense-reports-store';
import { getAccessToken } from '../services/auth-store';
import type { RouteContext } from '../services/router';
import type { Receipt, ExpenseType } from '../types/expense-tracker';

/**
 * Navigate to a path
 */
function navigateTo(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Format date for display in input[type="date"]
 */
function formatDateForInput(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0] ?? '';
  } catch {
    return '';
  }
}

/**
 * Create receipt image display
 */
function createReceiptImage(receiptId: string): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card rounded-2xl p-6';

  // Build image URL
  const imageUrl = `/api/receipts/${receiptId}/image`;

  container.innerHTML = `
    <div class="text-center">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-foreground">Receipt Image</h3>
        <button
          class="receipt-download-btn inline-flex items-center gap-2 px-3 py-1.5 text-sm
                 text-muted-foreground hover:text-primary hover:bg-primary/10
                 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Download receipt"
          data-testid="download-receipt-btn"
        >
          ${renderIcon('download', { size: 'sm' }).outerHTML}
          <span>Download</span>
        </button>
      </div>
      <div class="max-w-md mx-auto" data-image-container>
        <img
          src="${imageUrl}"
          alt="Receipt"
          class="w-full rounded-lg shadow-sm border border-border"
          style="max-height: 500px; object-fit: contain;"
          data-receipt-image
        />
      </div>
    </div>
  `;

  // Add error handler for receipt image
  const receiptImg = container.querySelector('[data-receipt-image]') as HTMLImageElement;
  const imageContainer = container.querySelector('[data-image-container]') as HTMLElement;
  if (receiptImg && imageContainer) {
    receiptImg.addEventListener('error', () => {
      const fallbackDiv = document.createElement('div');
      fallbackDiv.className = 'w-full h-64 flex items-center justify-center bg-muted rounded-lg';

      const textCenter = document.createElement('div');
      textCenter.className = 'text-center text-muted-foreground';

      textCenter.appendChild(renderIcon('alertTriangle', { size: 'lg' }));

      const errorText = document.createElement('p');
      errorText.className = 'mt-2';
      errorText.textContent = 'Failed to load image';
      textCenter.appendChild(errorText);

      fallbackDiv.appendChild(textCenter);
      imageContainer.innerHTML = '';
      imageContainer.appendChild(fallbackDiv);
    });
  }

  // Add download button handler
  const downloadBtn = container.querySelector('.receipt-download-btn') as HTMLButtonElement;
  if (downloadBtn) {
    downloadBtn.addEventListener('click', async () => {
      try {
        // First, get the presigned URL from our API
        const urlResponse = await fetch(`${imageUrl}?format=json`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });

        if (!urlResponse.ok) {
          throw new Error('Failed to get download URL');
        }

        const urlData = await urlResponse.json();

        // If we have a presigned S3 URL, open it directly (avoids CORS)
        if (urlData.url) {
          window.open(urlData.url, '_blank');
        } else {
          // For local storage, fetch with auth and create blob
          const response = await fetch(imageUrl, {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          });
          if (!response.ok) {
            throw new Error('Failed to download receipt');
          }

          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const filename = `receipt-${receiptId.slice(0, 8)}.jpg`;

          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
        }

        showToast({ type: 'success', message: 'Receipt downloaded successfully' });
      } catch (error) {
        console.error('Failed to download receipt:', error);
        showToast({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to download receipt',
        });
      }
    });
  }

  return container;
}

/**
 * Create read-only receipt details view (for non-DRAFT reports)
 */
function createReceiptReadOnlyView(receipt: Receipt, expenseReportId: string): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card rounded-2xl p-6 space-y-6';

  // Helper to format field
  const createField = (label: string, value: string | null | undefined): string => `
    <div>
      <dt class="text-sm font-medium text-muted-foreground">${label}</dt>
      <dd class="mt-1 text-foreground">${value || '—'}</dd>
    </div>
  `;

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('de-CH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Format amount
  const formatAmount = (amount: number | null, currency: string | null): string => {
    if (amount === null) return '—';
    return `${currency || 'CHF'} ${amount.toFixed(2)}`;
  };

  container.innerHTML = `
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-foreground">Receipt Details</h2>
      <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500">
        ${renderIcon('lock', { size: 'sm' }).outerHTML}
        Read Only
      </span>
    </div>
    <p class="text-sm text-muted-foreground mb-6">
      This receipt cannot be edited because the expense report has been submitted.
    </p>

    <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      ${createField('Vendor', receipt.vendor)}
      ${createField('Date', formatDate(receipt.receipt_date))}
      ${createField('Amount', formatAmount(receipt.amount, receipt.currency))}
      ${createField('Amount (CHF)', receipt.amount_chf !== null ? `CHF ${receipt.amount_chf.toFixed(2)}` : '—')}
      ${createField('VAT Rate', receipt.vat_percent !== null ? `${receipt.vat_percent}%` : '—')}
      ${createField('Expense Type', receipt.expense_type || '—')}
    </dl>

    ${receipt.notes ? `
      <div class="mt-4 pt-4 border-t border-border">
        <dt class="text-sm font-medium text-muted-foreground">Notes</dt>
        <dd class="mt-1 text-foreground">${receipt.notes}</dd>
      </div>
    ` : ''}

    <div class="flex justify-end pt-4">
      <button
        type="button"
        class="back-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
               text-foreground bg-secondary hover:bg-secondary/80
               rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
      >
        ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
        Back to Report
      </button>
    </div>
  `;

  // Bind back button
  const backBtn = container.querySelector('.back-btn');
  if (backBtn) {
    backBtn.addEventListener('click', () => navigateTo(`/expense-reports/${expenseReportId}`));
  }

  return container;
}

/**
 * Create receipt form
 */
function createReceiptForm(receipt: Receipt, expenseReportId: string): HTMLElement {
  const form = document.createElement('form');
  form.className = 'glass-card rounded-2xl p-6 space-y-6';
  form.setAttribute('novalidate', '');

  // Create form elements
  const vendorInput = new Input({
    id: 'vendor',
    name: 'vendor',
    type: 'text',
    value: receipt.vendor || '',
    placeholder: 'Enter vendor name',
    required: true,
  });

  const dateInput = new Input({
    id: 'receipt_date',
    name: 'receipt_date',
    type: 'date',
    value: formatDateForInput(receipt.receipt_date),
    required: true,
  });

  const amountInput = new Input({
    id: 'amount',
    name: 'amount',
    type: 'number',
    value: receipt.amount?.toString() || '',
    placeholder: '0.00',
    step: 0.01,
    min: 0,
    required: true,
  });

  const currencyInput = new Input({
    id: 'currency',
    name: 'currency',
    type: 'text',
    value: receipt.currency || 'CHF',
    placeholder: 'CHF',
    maxLength: 3,
    required: true,
  });

  // Track selected VAT rate
  let selectedVatRate = receipt.vat_percent ?? 8.1; // Default to standard rate

  // Create VAT selector container - will be populated after form creation
  const vatSelectorContainer = document.createElement('div');
  vatSelectorContainer.id = 'vat-selector-container';

  // Create hidden input to store the VAT value for form submission
  const vatHiddenInput = document.createElement('input');
  vatHiddenInput.type = 'hidden';
  vatHiddenInput.name = 'vat_percent';
  vatHiddenInput.value = String(selectedVatRate);

  // Create expense type selector
  const expenseTypeSelector = new ExpenseTypeSelector({
    id: 'expense_type',
    name: 'expense_type',
    value: receipt.expense_type,
    required: true,
  });

  // Create expense type help
  const expenseTypeHelp = createExpenseTypeHelp({
    mode: 'tooltip',
    showExamples: true,
    showAccountNumber: true,
  });

  const notesInput = new Input({
    id: 'notes',
    name: 'notes',
    type: 'text',
    value: receipt.notes || '',
    placeholder: 'Optional notes about this expense',
  });

  // Form header
  const formHeader = document.createElement('div');
  formHeader.className = 'border-b border-border pb-4 mb-6';
  formHeader.innerHTML = `
    <h3 class="text-lg font-semibold text-foreground">Receipt Details</h3>
    <p class="text-sm text-muted-foreground">Edit the extracted information or enter details manually</p>
  `;

  form.appendChild(formHeader);

  // Add form fields
  form.appendChild(createFormField(vendorInput.getElement(), {
    label: { text: 'Vendor' },
    required: true,
    helperText: { text: 'Name of the vendor or business' },
  }));

  form.appendChild(createFormField(dateInput.getElement(), {
    label: { text: 'Receipt Date' },
    required: true,
    helperText: { text: 'Date when the expense occurred' },
  }));

  const amountRow = document.createElement('div');
  amountRow.className = 'grid grid-cols-2 gap-4';

  amountRow.appendChild(createFormField(amountInput.getElement(), {
    label: { text: 'Amount' },
    required: true,
    helperText: { text: 'Total amount including VAT' },
  }));

  amountRow.appendChild(createFormField(currencyInput.getElement(), {
    label: { text: 'Currency' },
    required: true,
    helperText: { text: 'Three-letter currency code' },
  }));

  form.appendChild(amountRow);

  // Create and add the VAT selector
  // Build options conditionally to satisfy exactOptionalPropertyTypes
  const vatSelectorOptions: Parameters<typeof createVatSelector>[0] = {
    selectedRate: selectedVatRate,
    onChange: (rate) => {
      selectedVatRate = rate;
      vatHiddenInput.value = String(rate);
    },
  };
  if (receipt.ai_suggested_vat_rate != null) {
    vatSelectorOptions.aiSuggestedRate = receipt.ai_suggested_vat_rate;
  }
  const vatSelector = createVatSelector(vatSelectorOptions);
  vatSelectorContainer.appendChild(vatSelector);
  vatSelectorContainer.appendChild(vatHiddenInput);

  form.appendChild(createFormField(vatSelectorContainer, {
    label: { text: 'VAT Rate' },
    helperText: { text: 'Select the Swiss VAT rate for this expense' },
  }));

  // Expense type field with help
  const expenseTypeContainer = createFormField(expenseTypeSelector.getElement(), {
    label: { text: 'Expense Type' },
    required: true,
    helperText: { text: 'Select the appropriate expense category' },
  });

  // Add help icon next to the label
  const labelElement = expenseTypeContainer.querySelector('label');
  if (labelElement) {
    const helpContainer = document.createElement('span');
    helpContainer.className = 'ml-2';
    helpContainer.appendChild(expenseTypeHelp);
    labelElement.appendChild(helpContainer);
  }

  form.appendChild(expenseTypeContainer);

  form.appendChild(createFormField(notesInput.getElement(), {
    label: { text: 'Notes' },
    helperText: { text: 'Additional information about this expense' },
  }));

  // Action buttons
  const actionsContainer = document.createElement('div');
  actionsContainer.className = 'flex justify-end gap-3 pt-4 border-t border-border';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'px-4 py-2 text-muted-foreground hover:text-foreground transition-colors';
  cancelButton.textContent = 'Cancel';
  cancelButton.addEventListener('click', () => {
    navigateTo(`/expense-reports/${expenseReportId}`);
  });

  const saveButton = document.createElement('button');
  saveButton.type = 'submit';
  saveButton.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
  saveButton.innerHTML = `
    ${renderIcon('save', { size: 'sm' }).outerHTML}
    <span>Save Changes</span>
  `;

  actionsContainer.appendChild(cancelButton);
  actionsContainer.appendChild(saveButton);
  form.appendChild(actionsContainer);

  // Form submission handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(form);
    // Build data object conditionally to satisfy exactOptionalPropertyTypes
    const data: {
      vendor?: string;
      receipt_date?: string;
      amount?: number;
      currency?: string;
      vat_percent?: number;
      expense_type?: ExpenseType;
      notes?: string;
    } = {
      vendor: formData.get('vendor') as string,
      receipt_date: formData.get('receipt_date') as string,
      amount: parseFloat(formData.get('amount') as string),
      currency: formData.get('currency') as string,
    };
    const expenseType = expenseTypeSelector.getValue();
    if (expenseType != null) data.expense_type = expenseType as ExpenseType;
    const vatValue = formData.get('vat_percent');
    if (vatValue) data.vat_percent = parseFloat(vatValue as string);
    const notesValue = formData.get('notes') as string;
    if (notesValue) data.notes = notesValue;

    // Basic validation
    if (!data.vendor || !data.receipt_date || !data.amount || !data.currency || !data.expense_type) {
      showToast({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    // Disable form during submission
    const inputs = form.querySelectorAll('input, button, select');
    inputs.forEach(input => (input as HTMLInputElement).disabled = true);

    try {
      await expenseReportsStore.updateReceipt(receipt.id, data);
      showToast({ type: 'success', message: 'Receipt updated successfully' });
      navigateTo(`/expense-reports/${expenseReportId}`);
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update receipt'
      });
      // Re-enable form on error
      inputs.forEach(input => (input as HTMLInputElement).disabled = false);
    }
  });

  return form;
}

/**
 * Create loading state
 */
function createLoadingState(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';
  container.innerHTML = `
    <div class="animate-spin w-8 h-8 mx-auto mb-4 text-primary">
      ${renderIcon('loader', { size: 'lg' }).outerHTML}
    </div>
    <p class="text-muted-foreground">Loading receipt...</p>
  `;
  return container;
}

/**
 * Create error state
 */
function createErrorState(error: string, onBack: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';

  container.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
      ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
    </div>
    <h3 class="text-lg font-semibold text-foreground mb-2">Failed to load receipt</h3>
    <p class="text-muted-foreground mb-4">${error}</p>
  `;

  const backBtn = document.createElement('button');
  backBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
  backBtn.innerHTML = `
    ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
    <span>Back to Report</span>
  `;
  backBtn.addEventListener('click', onBack);
  container.appendChild(backBtn);

  return container;
}

/**
 * Create the receipt detail view
 */
export function receiptDetailView(context: RouteContext): HTMLElement {
  const expenseReportId = context.params.id!;
  const receiptId = context.params.receiptId!;

  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6 max-w-6xl mx-auto';

  // Back button
  const backBtn = document.createElement('button');
  backBtn.className = 'inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors';
  backBtn.innerHTML = `
    ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
    <span>Back to Report</span>
  `;
  backBtn.addEventListener('click', () => navigateTo(`/expense-reports/${expenseReportId}`));
  content.appendChild(backBtn);

  // Content container
  const contentContainer = document.createElement('div');
  contentContainer.id = 'receipt-content';
  content.appendChild(contentContainer);

  // Load receipt data
  const loadReceipt = async () => {
    try {
      contentContainer.appendChild(createLoadingState());

      // Fetch expense report with receipts
      await expenseReportsStore.fetchReport(expenseReportId);
      const state = expenseReportsStore.getState();

      if (state.error) {
        throw new Error(state.error);
      }

      if (!state.selectedReport) {
        throw new Error('Expense report not found');
      }

      // Find the receipt
      const receipt = state.selectedReport.receipts.find(r => r.id === receiptId);
      if (!receipt) {
        throw new Error('Receipt not found');
      }

      // Check if report is editable (DRAFT status only)
      const isEditable = state.selectedReport.status === 'DRAFT';

      // Clear loading and render receipt
      contentContainer.innerHTML = '';

      // Create two-column layout
      const grid = document.createElement('div');
      grid.className = 'grid grid-cols-1 lg:grid-cols-2 gap-6';

      // Left column - receipt image
      grid.appendChild(createReceiptImage(receiptId));

      // Right column - form (editable) or read-only view
      if (isEditable) {
        grid.appendChild(createReceiptForm(receipt, expenseReportId));
      } else {
        grid.appendChild(createReceiptReadOnlyView(receipt, expenseReportId));
      }

      contentContainer.appendChild(grid);

    } catch (error) {
      contentContainer.innerHTML = '';
      contentContainer.appendChild(
        createErrorState(
          error instanceof Error ? error.message : 'Unknown error',
          () => navigateTo(`/expense-reports/${expenseReportId}`)
        )
      );
    }
  };

  // Load receipt data
  setTimeout(loadReceipt, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/expense-reports',
    title: 'Edit Receipt',
  });
}

export default receiptDetailView;